import { WIDA_SCALE_RESET_YEAR } from '../../IndividualPlan/derive';
import { exitDate, monthYear, profileYear, sittingAfterExit } from '../profileText';
import type { ProfileAdministration, StudentProfile } from '../studentProfileApi';
import type { ExitBar } from './AccessCard';

export type MeasureKey = 'overall' | 'listening' | 'reading' | 'speaking' | 'writing';
export const MEASURES: { key: MeasureKey; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'listening', label: 'Listening' },
  { key: 'reading', label: 'Reading' },
  { key: 'speaking', label: 'Speaking' },
  { key: 'writing', label: 'Writing' },
];
export const SLOTS = 4;
export const RESET_FIRST_YEAR_END = WIDA_SCALE_RESET_YEAR;
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export type MissingReason = 'not_scored' | 'after_exit';
export type HistorySlot = { schoolYearEnd: number; value: number | null; missing?: MissingReason };
export type HistorySegment = { from: number; to: number; dotted: boolean };
export type MeasureHistory = { key: MeasureKey; label: string; slots: HistorySlot[]; segments: HistorySegment[]; headline: number | null; exit: number | null; belowExit: boolean };
export type FirstMeasure = { key: MeasureKey; label: string; value: number | null; exit: number | null; belowExit: boolean };
export type HistoryMarker = { after: number; label: string };
export type HistoryLegend = { exit: string | null; markers: string[]; missing: string[] };

export type ThresholdLabel = 'Exit' | 'Review';
export type AccessHistory =
  | { kind: 'none' }
  | { kind: 'first'; meta: string; note: string; measures: FirstMeasure[]; thresholdLabel: ThresholdLabel }
  | { kind: 'charts'; years: number[]; measures: MeasureHistory[]; markers: HistoryMarker[]; legend: HistoryLegend; meta: string; thresholdLabel: ThresholdLabel };

type Profile = Pick<StudentProfile, 'access' | 'cohorts' | 'standing' | 'elStatus'>;

function levelOf(admin: ProfileAdministration, key: MeasureKey): number | null {
  if (key === 'overall') return admin.composite;
  return admin.domains?.[key] ?? null;
}

function exitFor(bars: ExitBar[] | null, key: MeasureKey): number | null {
  return bars?.find((bar) => bar.key === key)?.minimum ?? null;
}

function calendarYear(admin: ProfileAdministration): number {
  return admin.month && admin.month >= 7 ? admin.schoolYearEnd - 1 : admin.schoolYearEnd;
}

function segmentsOf(slots: HistorySlot[]): HistorySegment[] {
  const present = slots.map((slot, i) => (slot.value == null ? -1 : i)).filter((i) => i >= 0);
  return present
    .slice(1)
    .map((to, i) => ({ from: present[i]!, to, dotted: to !== present[i]! + 1 }))
    .filter(({ from, to }) => !(slots[from]!.schoolYearEnd < RESET_FIRST_YEAR_END && slots[to]!.schoolYearEnd >= RESET_FIRST_YEAR_END));
}

function metaOf(sittings: ProfileAdministration[], beforeExit: boolean): string {
  const newest = sittings[0]!;
  const newestText = newest.month ? monthYear(newest.month, calendarYear(newest)) : profileYear(newest.schoolYearEnd);
  const months = new Set(sittings.map((s) => s.month ?? 0));
  const suffix = beforeExit ? ', before exit' : '';
  if (months.size === 1 && newest.month) return `Each ${FULL_MONTHS[newest.month - 1]} · newest ${newestText}${suffix}`;
  return `Newest ${newestText}${suffix}`;
}

export type ExitOutcome = 'exits' | 'eligible_for_review';

export function accessHistoryOf(profile: Profile, bars: ExitBar[] | null, stateName?: string, outcome: ExitOutcome = 'exits'): AccessHistory {
  if (profile.access.scale === 'alternate_access') return { kind: 'none' };
  const all = profile.access.administrations.filter((a) => a.scale === 'access');
  if (all.length === 0) return { kind: 'none' };
  const newcomer = profile.cohorts.includes('newcomer');
  const exited = (profile.standing === 'monitoring' || profile.standing === 'former' || profile.standing === 'departed') && profile.elStatus.exitYearEnd ? profile.elStatus : null;
  const kept = exited ? all.filter((a) => !sittingAfterExit(a, exited.exitYearEnd!, exited.exitMonth ?? 0)) : all;
  if (kept.length === 0) return { kind: 'none' };
  const marks = (value: number | null, exit: number | null) => outcome === 'exits' && value != null && exit != null && value < exit && !newcomer;
  const thresholdLabel: ThresholdLabel = outcome === 'exits' ? 'Exit' : 'Review';

  const blankedAfterExit = kept.length < all.length;
  const keptYears = new Set(kept.map((a) => a.schoolYearEnd));
  if (keptYears.size === 1) {
    const only = kept[0]!;
    const when = only.month ? monthYear(only.month, calendarYear(only)) : profileYear(only.schoolYearEnd);
    const grade = only.grade ? `, grade ${only.grade}` : '';
    const exitText = exited ? `Exited ${exitDate(exited.exitYearEnd!, exited.exitMonth ?? 0)}.` : '';
    const note = exited
      ? `${exitText}${blankedAfterExit ? ' No ACCESS after exit.' : ''}`
      : only.month
        ? `Charts start with the second ACCESS, in ${FULL_MONTHS[only.month - 1]} ${calendarYear(only) + 1}.`
        : 'Charts start with the second ACCESS.';
    return {
      kind: 'first',
      thresholdLabel,
      meta: `First ACCESS · ${when}${grade}${blankedAfterExit ? ', before exit' : ''}`,
      note,
      measures: MEASURES.map(({ key, label }) => {
        const value = levelOf(only, key);
        const exit = exitFor(bars, key);
        return { key, label, value, exit, belowExit: marks(value, exit) };
      }),
    };
  }

  const lastKeptYear = Math.max(...kept.map((a) => a.schoolYearEnd));
  const cutInExitYear = exited != null && kept.length < all.filter((a) => a.schoolYearEnd <= exited.exitYearEnd!).length;
  const exitBoundary = exited ? Math.max(lastKeptYear, cutInExitYear ? exited.exitYearEnd! - 1 : exited.exitYearEnd!) : null;
  const lastYear = exitBoundary != null && blankedAfterExit ? Math.min(exitBoundary + 1, lastKeptYear + SLOTS - 1) : lastKeptYear;
  const years = Array.from({ length: SLOTS }, (_, i) => lastYear - (SLOTS - 1) + i);
  const byYear = new Map<number, ProfileAdministration>();
  for (const admin of kept) {
    if (!byYear.has(admin.schoolYearEnd)) byYear.set(admin.schoolYearEnd, admin);
  }
  const newestKept = kept[0]!;
  const blanked = blankedAfterExit;
  const measures = MEASURES.map(({ key, label }) => {
    const slots = years.map((schoolYearEnd) => {
      const admin = byYear.get(schoolYearEnd);
      const value = admin ? levelOf(admin, key) : null;
      if (value != null) return { schoolYearEnd, value };
      const missing: MissingReason = exitBoundary != null && blanked && schoolYearEnd > exitBoundary ? 'after_exit' : 'not_scored';
      return { schoolYearEnd, value, missing };
    });
    const headline = levelOf(newestKept, key);
    const exit = exitFor(bars, key);
    return { key, label, slots, segments: segmentsOf(slots), headline, exit, belowExit: marks(headline, exit) };
  });
  const shownKept = kept.filter((a) => years.includes(a.schoolYearEnd));
  const spansReset = shownKept.some((a) => a.schoolYearEnd < RESET_FIRST_YEAR_END) && shownKept.some((a) => a.schoolYearEnd >= RESET_FIRST_YEAR_END);
  const markers: HistoryMarker[] = [];
  if (spansReset) markers.push({ after: RESET_FIRST_YEAR_END - 1, label: `New ACCESS scale from ${profileYear(RESET_FIRST_YEAR_END)}` });
  if (exitBoundary != null && blankedAfterExit) {
    const exitText = `Exited ${exitDate(exited!.exitYearEnd!, exited!.exitMonth ?? 0)}`;
    markers.push(exitBoundary < lastYear ? { after: exitBoundary, label: exitText } : { after: lastYear, label: `${exitText}, no ACCESS after exit` });
  }
  markers.sort((a, b) => a.after - b.after);
  const hasExit = measures.some((m) => m.exit != null);
  const reasons = new Set(measures.flatMap((m) => m.slots.map((slot) => slot.missing)).filter((r): r is MissingReason => r != null));
  const missing = [...(reasons.has('not_scored') ? ['– Not scored'] : []), ...(reasons.has('after_exit') ? ['· No ACCESS after exit'] : [])];
  return {
    kind: 'charts',
    thresholdLabel,
    years,
    measures,
    markers,
    legend: { exit: hasExit ? `${stateName ?? 'District'} ${outcome === 'exits' ? 'exit' : 'review'} criteria` : null, markers: markers.map((m) => m.label), missing },
    meta: metaOf(kept, blanked),
  };
}
